import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Tiny module-level resource cache — the app deliberately has no React Query, so
 * this is the minimal equivalent: data fetched under a key survives component
 * unmount/remount (until a full page reload). That makes Settings tab switches
 * instant — a tab's component can unmount and remount without refetching.
 *
 * There is intentionally NO staleness / auto-revalidation: data persists until a
 * page reload or an explicit refetch() (retry buttons, after a mutation). This
 * matches the desired "once it's loaded it's there unless I reload" behaviour.
 */

const store = new Map<string, unknown>();

/** Wipe every cached resource. Call on logout so a different user signing in
 *  (client-side, no page reload) never reads the previous user's cached data. */
export function clearResourceCache(): void {
  store.clear();
}

export interface CachedResource<T> {
  data: T | undefined;
  loading: boolean;
  error: string | null;
  /** Force a network fetch and refresh the cache (bypasses the cached value). */
  refetch(): void;
}

/**
 * Fetch-once-and-cache a resource by `key`.
 *
 * On mount: if `key` is already cached, the value is served synchronously (no
 * network call, no loading flash); otherwise it fetches once and stores the
 * result. `fetcher` MUST be stable (wrap it in useCallback) — its identity is an
 * effect dependency. `enabled=false` defers fetching until it flips true.
 */
export function useCachedResource<T>(
  key: string,
  fetcher: () => Promise<T>,
  enabled = true,
): CachedResource<T> {
  const [data, setData] = useState<T | undefined>(() => store.get(key) as T | undefined);
  const [loading, setLoading] = useState(() => enabled && !store.has(key));
  const [error, setError] = useState<string | null>(null);
  // Monotonic id so a slow in-flight fetch can't clobber a newer one.
  const reqId = useRef(0);

  const fetchNow = useCallback(() => {
    const id = ++reqId.current;
    setLoading(true);
    setError(null);
    void fetcher()
      .then((result) => {
        store.set(key, result);
        if (id === reqId.current) setData(result);
      })
      .catch((e: unknown) => {
        if (id === reqId.current) setError((e as Error)?.message || 'Could not load this section');
      })
      .finally(() => {
        if (id === reqId.current) setLoading(false);
      });
  }, [key, fetcher]);

  useEffect(() => {
    if (!enabled) return;
    if (store.has(key)) {
      // Cache hit — serve synchronously, skip the network entirely.
      setData(store.get(key) as T);
      setError(null);
      setLoading(false);
    } else {
      fetchNow();
    }
  }, [enabled, key, fetchNow]);

  return { data, loading, error, refetch: fetchNow };
}
